import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Eye, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { PerformanceData, RecruiterPerformance } from '@/hooks/usePerformanceData';

interface RecruiterOverviewProps {
  data: PerformanceData | null;
  selectedRecruiterId: string | null;
  onSelectRecruiter: (id: string | null) => void;
}

export function RecruiterOverview({ data, selectedRecruiterId, onSelectRecruiter }: RecruiterOverviewProps) {
  if (!data) return null;

  const recruitersToShow = selectedRecruiterId
    ? data.recruiters.filter((r) => r.recruiterId === selectedRecruiterId)
    : data.recruiters.filter((r) => r.totalCandidates > 0 || r.openPositions > 0);

  const getPerformanceIndicator = (value: number, average: number) => {
    const diff = value - average;
    const percentDiff = average > 0 ? (diff / average) * 100 : 0;

    if (percentDiff > 10) {
      return { icon: TrendingUp, color: 'text-emerald-500', label: 'Above avg' };
    } else if (percentDiff < -10) {
      return { icon: TrendingDown, color: 'text-destructive', label: 'Below avg' };
    }
    return { icon: Minus, color: 'text-muted-foreground', label: 'Average' };
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Recruiter Overview</CardTitle>
            <CardDescription>
              {selectedRecruiterId
                ? 'Individual recruiter performance'
                : 'Active recruiters and their current workload'}
            </CardDescription>
          </div>
          {selectedRecruiterId && (
            <Button variant="outline" size="sm" onClick={() => onSelectRecruiter(null)}>
              View All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {recruitersToShow.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No recruiters with activity in the selected period
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recruiter</TableHead>
                <TableHead className="text-center">Open Positions</TableHead>
                <TableHead className="text-center">Candidates</TableHead>
                <TableHead className="text-center">Interviewed</TableHead>
                <TableHead className="text-center">Selected</TableHead>
                <TableHead className="text-center">Conversion Rate</TableHead>
                <TableHead className="text-center">Performance</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recruitersToShow.map((recruiter) => {
                const perfIndicator = getPerformanceIndicator(
                  recruiter.conversionRate,
                  data.teamAverages.conversionRate
                );
                const PerfIcon = perfIndicator.icon;

                return (
                  <TableRow key={recruiter.recruiterId}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{recruiter.recruiterName}</div>
                        <div className="text-sm text-muted-foreground">{recruiter.recruiterEmail}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{recruiter.openPositions}</Badge>
                    </TableCell>
                    <TableCell className="text-center">{recruiter.totalCandidates}</TableCell>
                    <TableCell className="text-center">{recruiter.candidatesInterviewed}</TableCell>
                    <TableCell className="text-center">{recruiter.candidatesSelected}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center gap-2">
                        <Progress value={recruiter.conversionRate} className="w-16 h-2" />
                        <span className="text-sm">{recruiter.conversionRate.toFixed(1)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <PerfIcon className={`h-4 w-4 ${perfIndicator.color}`} />
                        <span className={`text-xs ${perfIndicator.color}`}>{perfIndicator.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSelectRecruiter(recruiter.recruiterId)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
